import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import Radio from "@mui/material/Radio";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import StripeCard from "../StripeComponents/StripeCard";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { listConnectAccounts, setPayoutAccount } from "../../network/oireachtasEventDay";

// Stripe payout account for an Oireachtas event (the gap that forced staging
// to be hand-wired in the DB): checkout hard-gates on connectedAccountId —
// without one, NO registrations can pay. Lists the event ORGANIZER's connected
// accounts (PaymentFies radio convention) and assigns one; the server refuses
// accounts that don't belong to this event's organizer. Unlike the feis
// screens, accounts with no external bank account yet (OAuth-linked Standard
// accounts — FLC-94) are SHOWN with a note, never silently hidden.
// Adding/creating accounts stays on the organizer's own Stripe flows — the
// OAuth return URI doesn't whitelist this page.
const OireachtasStripe = () => {
  const [eventDoc, setEventDoc] = useState(null);
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const eventRes = await serviceConsumer(
          "GET",
          `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`
        );
        const ev = eventRes.event;
        setEventDoc(ev);
        setSelected(ev?.connectedAccountId || "");
        if (ev?.organizer_id) {
          const res = await listConnectAccounts(ev.organizer_id);
          setCards(res?.result || []);
        }
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load Stripe accounts");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const assign = async (cardId) => {
    try {
      setBusy(true);
      const res = await setPayoutAccount(eventId, cardId);
      setSelected(cardId);
      ShowToastMessage("success", res.message || "Payout account assigned");
    } catch (e) {
      ShowToastMessage("error", e?.messages || "Could not assign the account");
    } finally {
      setBusy(false);
    }
  };

  const eventDate = (eventDoc?.eventDays && eventDoc.eventDays[0]) || eventDoc?.entriesCloseDate;
  const hasBank = (card) => card?.externalAccount?.currency && card?.externalAccount?.last4;

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={eventDoc?.logo}
        eventName={eventDoc?.name}
        eventType={eventDoc?.eventType}
        eventDate={eventDate}
        loading={loading}
        onBackClick={() => navigate(`/oireachtas/manage?id=${eventId}`)}
      />
      {!loading && (
        <div className="db-body pb-5">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Registration payments route to this account (Stripe Connect destination). Until one is
            assigned, checkout refuses every registration for this event. Accounts are the event
            organizer's — new accounts are added from the organizer's own Stripe screens.
          </div>

          {!selected && cards.length > 0 && (
            <div className="mt-3 error-class" style={{ fontSize: "13px", color: "#d32f2f" }}>
              No payout account assigned — this event cannot accept payments yet.
            </div>
          )}

          <div className="mt-4">
            {cards.map((card) => (
              <div key={card?._id} className="d-flex align-items-center gap-3 mt-3">
                <Radio
                  checked={String(selected) === String(card?._id)}
                  disabled={busy}
                  onChange={() => assign(card?._id)}
                />
                {hasBank(card) ? (
                  <StripeCard card={card} />
                ) : (
                  <div className="card" style={{ width: "fit-content" }}>
                    <div className="card-body">
                      <p className="card-cont mb-0">
                        Stripe account {card?.accountId}
                        <span className="grey-42" style={{ marginLeft: "8px", fontSize: "12px" }}>
                          no external bank account on file
                          {card?.payoutEnabled ? "" : " · payouts not enabled"}
                        </span>
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {cards.length === 0 && (
            <div className="d-flex justify-content-center my-5 grey-42">
              The organizer has no connected Stripe accounts yet.
            </div>
          )}
        </div>
      )}
      <Help />
      <ToastContainer />
    </>
  );
};

export default OireachtasStripe;
